import { useEffect, useRef, useState } from "react";
import { Button, CircularProgress, IconButton, Tooltip } from "@mui/material";
import { MdDownload } from "react-icons/md";
import { requestDownload } from "api/files";
import { useToast } from "hooks/useToast";

type Props = {
  requestUrl: string;
  label?: string;
  iconOnly?: boolean;
  disabled?: boolean;
  disabledReason?: string;
};

export default function FileDownloadButton({
  requestUrl,
  label = "Descargar archivo",
  iconOnly = false,
  disabled = false,
  disabledReason = "Archivo no disponible",
}: Props) {
  const [loading, setLoading] = useState(false);
  const attemptRef = useRef<symbol | null>(null);
  const { showSnackBar } = useToast();

  useEffect(() => () => { attemptRef.current = null; }, []);

  async function handleDownload() {
    if (disabled || attemptRef.current) return;
    const attempt = Symbol();
    attemptRef.current = attempt;
    setLoading(true);
    try {
      const download = await requestDownload(requestUrl);
      if (attemptRef.current !== attempt) return;

      const link = document.createElement("a");
      link.href = download.url;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.download = "";
      link.referrerPolicy = "no-referrer";
      document.body.appendChild(link);
      try {
        link.click();
      } finally {
        link.remove();
      }
    } catch (error) {
      if (attemptRef.current === attempt) {
        showSnackBar(error instanceof Error ? error.message : "No se pudo solicitar la descarga.", "error");
      }
    } finally {
      if (attemptRef.current === attempt) {
        attemptRef.current = null;
        setLoading(false);
      }
    }
  }

  const downloadLabel = loading ? "Preparando descarga..." : label;

  return (
    <Tooltip title={disabled ? disabledReason : iconOnly ? downloadLabel : ""}>
      <span>
        {iconOnly ? (
          <IconButton
            size="small"
            aria-label={downloadLabel}
            aria-busy={loading}
            disabled={disabled || loading}
            onClick={handleDownload}
          >
            {loading ? <CircularProgress size={16} /> : <MdDownload />}
          </IconButton>
        ) : (
          <Button size="small" startIcon={<MdDownload />} disabled={disabled || loading} onClick={handleDownload}>
            {downloadLabel}
          </Button>
        )}
      </span>
    </Tooltip>
  );
}
